import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * DAVIDsTEA — redeeming a LoyaltyLion reward opens the cart drawer instead of the cart page.
 *
 * LoyaltyLion claims a free-product reward over AJAX (`POST /cart/add.js`) and then navigates
 * the browser to /cart, which throws away the page the shopper was on and skips the drawer this
 * theme opens for every one of its own add-to-carts.
 *
 * The redirect is not something to fight. Their SDK gates it:
 *
 *   … || vc.listenerCount("cart.changed") > 0 || I("/cart")
 *
 * `I("/cart")` is the navigation, and it is skipped the moment anything is listening for their
 * `cart.changed` event — their own escape hatch for themes with an AJAX cart. `on` is part of
 * the public API their loader buffers (`_push configure bootstrap shutdown on removeListener
 * authenticateCustomer`), so this can be registered before the SDK itself has finished loading.
 *
 * Claiming the event leaves this file owing the theme the update LoyaltyLion would otherwise
 * have delivered by reloading the page. That is one event:
 *
 *   `cart-drawer.js`          opens on a CartLinesUpdateEvent whose action is 'add'
 *   `component-cart-items.js` re-renders its own section when the event carries no `sections`,
 *                             so the contents need no fetching or morphing here
 *   `cart-icon.js`            reads `detail.itemCount` for the bubble, which is the one number
 *                             this file does have to go and get
 *
 * `action: 'add'` is safe: all three of the SDK's `cart.changed` emissions sit directly after an
 * add, and removing a line (`removeItemsFromCart`) does not emit it at all — checked against the
 * shipped bundle rather than assumed, because announcing an add on a removal would pop the
 * drawer open at the worst possible moment.
 *
 * `lines` is empty on purpose. The event's consumers in this theme use it for optimistic UI that
 * nothing here needs, and the only honest value would be the variant LoyaltyLion picked, which
 * the `cart.changed` payload does not carry.
 *
 * Loaded from snippets/scripts.liquid on the `page.loyaltylion` template alone: redemption in
 * this store happens on that page and nowhere else. If a launcher or widget that can claim
 * rewards is ever enabled site-wide, drop the template guard around the script tag rather than
 * copying this file — the listener is idempotent and harmless on a page with no rewards on it.
 *
 *
 * The other half of the same bargain: telling LoyaltyLion when the theme changes the cart.
 *
 * Claim a reward, change your mind, take it out of the drawer, and the rewards page behind the
 * drawer went on showing the reward as spent until it was reloaded by hand. LoyaltyLion had no
 * way of knowing: their SDK does not watch the cart, and this theme removes a line over AJAX
 * without ever reloading the page. Their own guidance for exactly this case is that the theme
 * has to hand the new cart over —
 * https://developers.loyaltylion.com/docs/features/in-cart-rewards, whose reference theme says
 * `setCartState` "expects a full Shopify cart object as is returned from the /cart.js Ajax API
 * call" whenever non-LoyaltyLion code changes the cart without a page reload.
 *
 * `setCartState` is part of the API their loader buffers, alongside `on` and `configure`, so it
 * can be called before the SDK has finished starting up and is replayed onto the real instance —
 * confirmed in the shipped loader, which keeps a `bufferedClientCartStates` list and pushes each
 * entry through once bootstrap completes.
 *
 * `ui.refresh()` then repaints the components, which is the documented live-updating call
 * (https://developers.loyaltylion.com/sdk/live-updating). State first, paint second: refreshing
 * before the SDK has the new cart would only redraw the stale answer.
 */

const SDK_WAIT_MS = 15000;
const SDK_POLL_MS = 250;

/**
 * Announces a LoyaltyLion redemption to the theme as an ordinary add-to-cart.
 */
async function announceCartAdd() {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();
    deferred.resolve({ detail: { itemCount: cart.item_count } });
  } catch (error) {
    // The drawer has already opened and the cart section refetches itself either way; only the
    // header bubble depends on this number, so a stale count beats a rejected promise, which
    // every listener would log.
    console.warn('[dt-loyaltylion-cart] Could not read the cart after a redemption:', error);
    deferred.resolve({ detail: {} });
  }
}

/**
 * Hands LoyaltyLion the cart the theme has just written, then asks it to repaint.
 *
 * Every line change goes through here, not just removals. An add is just as capable of leaving
 * the widget stale, and `setCartState` wants the whole cart either way, so there is nothing to
 * gain from telling them about one kind of change and not the other.
 *
 * Runs after the event's own promise settles. That promise is how this theme signals that the
 * write to /cart/change has actually come back; reading /cart.js before it resolves is a race
 * that returns the cart as it was.
 */
async function syncCartToLoyaltyLion() {
  const lion = window.loyaltylion;
  if (!lion) return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();

    lion.setCartState?.(cart);
    lion.ui?.refresh?.();
  } catch (error) {
    // A stale widget is the status quo this is trying to improve on, so a failure here costs
    // nothing beyond that. Nothing is thrown at the theme's own cart flow, which has already
    // finished by this point.
    console.warn('[dt-loyaltylion-cart] Could not hand the cart to LoyaltyLion:', error);
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;

  if (!promise) {
    syncCartToLoyaltyLion();
    return;
  }

  // `finally`, not `then`: a line the shopper removed is gone from the cart whether or not the
  // theme's own follow-up work succeeded, and that is the case this exists for.
  promise.finally(syncCartToLoyaltyLion).catch(() => {});
});

/**
 * The app embed defines `window.loyaltylion` from an inline script, so it is normally there
 * before this module runs. Polling covers the case where the embed is placed later in the
 * document, and gives up rather than running forever when the embed is switched off entirely.
 */
function whenLoyaltyLionReady() {
  if (window.loyaltylion?.on) {
    window.loyaltylion.on('cart.changed', announceCartAdd);
    return;
  }

  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (window.loyaltylion?.on) {
      clearInterval(timer);
      window.loyaltylion.on('cart.changed', announceCartAdd);
    } else if (Date.now() - startedAt > SDK_WAIT_MS) {
      clearInterval(timer);
    }
  }, SDK_POLL_MS);
}

whenLoyaltyLionReady();
